import { CustomHeader } from "@/controls/ag-grid/components/HeaderWithEditButton";
import { StrandData } from "@/pages/caster/models/StrandData";
import { Checkbox } from "@mui/material";
import { ColDef, ColGroupDef } from "ag-grid-community";

interface HousingProperty {
	property: string;
	field: string;
	type: "listbox" | "checkbox" | "input" | "autocomplete";
	readOnly?: boolean;
}

export const useHousingTableData = (
	strands: StrandData[],
	edit: (id: string) => void,
) => {
	const housingProperties: HousingProperty[] = [
		{ property: "Housing", field: "HousingName", type: "listbox" },
		{
			property: "Housing First",
			field: "ForceHeatsOnHousing",
			type: "checkbox",
		},
		{
			property: "Housing Heats",
			field: "HeatsOnHousing",
			type: "input",
			readOnly: true,
		},
		{ property: "Mold", field: "MoldName", type: "autocomplete" },
		{ property: "Size", field: "MoldSizeName", type: "input", readOnly: true },
		{ property: "Mold First", field: "ForceHeatsOnMold", type: "checkbox" },
		{
			property: "Mold Heats",
			field: "HeatsOnMold",
			type: "input",
			readOnly: true,
		},
	];

	const dataByStrand: Record<string, StrandData> = strands.reduce(
		(acc, cur) => {
			acc[cur.Strand?.toString()] = cur;
			return acc;
		},
		{} as Record<string, StrandData>,
	);

	const rowData = housingProperties.map((prop) => ({
		id: prop.property,
		Property: prop.property,
		s1: dataByStrand["1"]
			? dataByStrand["1"][prop.field as keyof StrandData]
			: null,
		s2: dataByStrand["2"]
			? dataByStrand["2"][prop.field as keyof StrandData]
			: null,
		s3: dataByStrand["3"]
			? dataByStrand["3"][prop.field as keyof StrandData]
			: null,
		type: prop.type,
		field: prop.field,
		readOnly: prop.readOnly || false,
	}));

	const cellRenderer = (params: any) => {
		if (params.data.type === "checkbox") {
			return <Checkbox color="primary" checked={!!params.value} disabled />;
		}
		return <span>{params.value != null ? params.value : ""}</span>;
	};

	const columns: (ColGroupDef | ColDef)[] = [
		{
			field: "Property",
			headerName: "",
			flex: 1,
			minWidth: 115,
		},
		{
			field: "s1",
			headerName: "S1",
			flex: 1,
			minWidth: 84,
			headerComponent: CustomHeader,
			headerComponentParams: {
				editCallback: (colId: string) => {
					edit(colId);
				},
			},
			cellRenderer: cellRenderer,
			cellStyle: {
				display: "flex",
				justifyContent: "center",
			},
		},
		{
			field: "s2",
			headerName: "S2",
			flex: 1,
			minWidth: 84,
			headerComponent: CustomHeader,
			headerComponentParams: {
				editCallback: (colId: string) => {
					edit(colId);
				},
			},
			cellRenderer: cellRenderer,
			cellStyle: {
				display: "flex",
				justifyContent: "center",
			},
		},
		{
			field: "s3",
			headerName: "S3",
			flex: 1,
			minWidth: 85,
			headerComponent: CustomHeader,
			headerComponentParams: {
				editCallback: (colId: string) => {
					edit(colId);
				},
			},
			cellRenderer: cellRenderer,
			cellStyle: {
				display: "flex",
				justifyContent: "center",
			},
		},
	];

	const defaultColDef: ColDef = {
		filter: false,
		floatingFilter: false,
		sortable: false,
		resizable: false,
		editable: false,
	};

	return { columns, defaultColDef, rowData };
};
